import { useEffect, useRef, useState } from 'react';

/**
 * BTC 실시간 시세 그래프 (2026-09-19)
 *
 * 왜 필요했나
 *   "1분 뒤 오를까?" 라고만 묻고 그래프가 없었다. 무엇을 보고 고르라는 건지 알 수 없었다.
 *
 * 어디서 값을 받나
 *   ① 바이낸스 실시간 체결 스트림 — 초 단위로 들어온다
 *   ② 막히면 부모가 넘겨주는 현재가로 대신 그린다 (게임 화면이 3초마다 새로 읽는다)
 *   둘 다 안 되면 그래프를 아예 그리지 않는다. 가짜 선을 그리면 안 된다.
 *
 * 무엇을 보여주나
 *   · 지금까지의 시세 흐름
 *   · 기준가(라운드 시작가) — 이 선 위로 끝나면 '오른다' 가 맞은 것이다
 *   · 내가 고른 지점 — 언제, 얼마에, 어느 쪽으로 걸었는지
 */

type Pt = { t: number; p: number };

export type Mark = { t: number; p: number; dir: 'up' | 'down' } | null;

const WINDOW_MS = 150_000;   // 2분 30초. 1분 라운드를 앞뒤로 볼 수 있게.

export function BtcLiveChart({ openPrice, fallbackPrice, mark, height = 168 }: {
  openPrice?: number | null;
  fallbackPrice?: number | null;
  mark?: Mark;
  height?: number;
}) {
  const [pts, setPts] = useState<Pt[]>([]);
  const [live, setLive] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  // ── 실시간 연결
  useEffect(() => {
    let dead = false;

    // 처음 한 번은 과거 몇 분을 채워 넣는다. 빈 화면에서 선이 자라나면 답답하다.
    (async () => {
      try {
        const r = await fetch(
          'https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1s&limit=150');
        const j = await r.json();
        if (dead || !Array.isArray(j)) return;
        setPts(j.map((k: any) => ({ t: Number(k[0]), p: Number(k[4]) })));
      } catch { /* 못 받아도 아래 실시간으로 채워진다 */ }
    })();

    try {
      const ws = new WebSocket('wss://stream.binance.com:9443/ws/btcusdt@trade');
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          const p = Number(d.p);
          if (!p) return;
          if (!dead) {
            setLive(true);
            setPts(v => [...v, { t: Number(d.T) || Date.now(), p }].slice(-600));
          }
        } catch { /* 한 건 깨져도 다음 게 온다 */ }
      };
      ws.onerror = () => setLive(false);
      ws.onclose = () => setLive(false);
    } catch { /* 웹소켓이 막힌 환경 — 아래 대체 경로로 간다 */ }

    return () => { dead = true; try { wsRef.current?.close(); } catch {} };
  }, []);

  // ── 실시간이 안 되면 부모가 주는 값으로 그린다
  useEffect(() => {
    if (live || !fallbackPrice) return;
    setPts(v => {
      const last = v[v.length - 1];
      if (last && Math.abs(last.p - fallbackPrice) < 1e-9) return v;
      return [...v, { t: Date.now(), p: Number(fallbackPrice) }].slice(-600);
    });
  }, [fallbackPrice, live]);

  const now = Date.now();
  const data = pts.filter(x => now - x.t < WINDOW_MS);
  if (data.length < 2) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 12, background: 'rgba(255,255,255,.04)', fontSize: 13, opacity: .6,
      }}>
        시세를 불러오는 중…
      </div>
    );
  }

  const W = 320, H = height;
  const PADT = 10, PADB = 18;
  const t0 = data[0].t, t1 = Math.max(data[data.length - 1].t, t0 + 1);

  const ps = data.map(d => d.p);
  if (openPrice) ps.push(Number(openPrice));
  if (mark) ps.push(mark.p);
  let lo = Math.min(...ps), hi = Math.max(...ps);
  const pad = Math.max((hi - lo) * 0.18, hi * 0.00015);   // 선이 위아래 끝에 붙지 않게
  lo -= pad; hi += pad;

  const X = (t: number) => ((t - t0) / (t1 - t0)) * W;
  const Y = (p: number) => PADT + (1 - (p - lo) / (hi - lo)) * (H - PADT - PADB);

  const line = data.map((d, i) => `${i ? 'L' : 'M'}${X(d.t).toFixed(1)},${Y(d.p).toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H - PADB} L0,${H - PADB} Z`;

  const cur = data[data.length - 1].p;
  const base = Number(openPrice || data[0].p);
  const diff = cur - base;
  const pct = base ? (diff / base) * 100 : 0;
  const up = diff >= 0;
  const col = up ? '#22C55E' : '#EF4444';

  const nf = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ borderRadius: 12, background: 'rgba(255,255,255,.04)', padding: '10px 10px 6px' }}>
      {/* 현재가 · 기준가 대비 */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <b style={{ fontSize: 20, letterSpacing: -.3 }}>${nf(cur)}</b>
        <span style={{ color: col, fontSize: 13, fontWeight: 800 }}>
          {up ? '▲' : '▼'} {Math.abs(pct).toFixed(3)}%
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, opacity: .6 }}>
          {live ? '● 실시간' : '○ 3초 간격'}
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
           style={{ display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id="btcg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={col} stopOpacity=".28" />
            <stop offset="100%" stopColor={col} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* 기준가 — 이 선 위에서 끝나면 '오른다' 가 맞은 것이다 */}
        {openPrice ? (
          <>
            <line x1="0" x2={W} y1={Y(Number(openPrice))} y2={Y(Number(openPrice))}
                  stroke="#9CA3AF" strokeWidth="1" strokeDasharray="4 4" opacity=".7" />
            <text x="2" y={Y(Number(openPrice)) - 4} fill="#9CA3AF" fontSize="9">
              기준가 ${nf(Number(openPrice))}
            </text>
          </>
        ) : null}

        <path d={area} fill="url(#btcg)" />
        <path d={line} fill="none" stroke={col} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />

        {/* 지금 값 */}
        <circle cx={X(data[data.length - 1].t)} cy={Y(cur)} r="3.5" fill={col} />

        {/* 내가 고른 지점 */}
        {mark && mark.t >= t0 ? (
          <>
            <line x1={X(mark.t)} x2={X(mark.t)} y1={PADT} y2={H - PADB}
                  stroke={mark.dir === 'up' ? '#22C55E' : '#EF4444'}
                  strokeWidth="1" strokeDasharray="3 3" opacity=".8" />
            <circle cx={X(mark.t)} cy={Y(mark.p)} r="5"
                    fill={mark.dir === 'up' ? '#22C55E' : '#EF4444'}
                    stroke="#fff" strokeWidth="1.5" />
            <text x={Math.min(X(mark.t) + 6, W - 60)} y={Y(mark.p) - 8}
                  fill="#fff" fontSize="9.5" fontWeight="700">
              내 선택 {mark.dir === 'up' ? '⬆' : '⬇'}
            </text>
          </>
        ) : null}
      </svg>

      {mark ? (
        <div style={{ fontSize: 11, opacity: .75, marginTop: 2 }}>
          ${nf(mark.p)} 에서 <b style={{ color: mark.dir === 'up' ? '#22C55E' : '#EF4444' }}>
            {mark.dir === 'up' ? '오른다' : '내린다'}
          </b> 선택 · 지금 {cur === mark.p ? '변동 없음'
            : (mark.dir === 'up') === (cur > mark.p) ? '맞는 방향' : '반대 방향'}
        </div>
      ) : null}
    </div>
  );
}
